import { Component, input, output, signal, OnInit, OnDestroy, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ModalComponent } from '../modal/modal.component';
import { ButtonComponent } from '../button/button.component';
import { InputGeneralSelectComponent } from '../../../inputs/input-general-select/input-general-select.component';
import { InputGeneralCalendarComponent } from '../../../inputs/input-general-calendar/input-general-calendar.component';
import { InputGeneralCheckboxComponent } from '../../../inputs/input-general-checkbox/input-general-checkbox.component';
import { InputGeneralComponent } from '../../../inputs/input-general/input-general.component';
import { FilterDefinition, FilterModel } from './filter.model';

@Component({
  selector: 'app-filter-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    ModalComponent,
    ButtonComponent,
    InputGeneralSelectComponent,
    InputGeneralCalendarComponent,
    InputGeneralCheckboxComponent,
    InputGeneralComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterModalComponent),
      multi: true,
    },
  ],
  templateUrl: './filter-modal.component.html',
})
export class FilterModalComponent implements OnInit, OnDestroy, ControlValueAccessor {
  isOpen = input(false);
  title = input('Filtros');
  filterDefinitions = input<FilterDefinition[]>([]);
  initialValue = input<FilterModel>({});

  filterChange = output<FilterModel>();
  closed = output<void>();

  form!: FormGroup;
  private onChange: (value: FilterModel) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.onTouched();
  }

  private initForm(): void {
    const group: { [key: string]: FormControl } = {};
    const initial = this.initialValue();

    this.filterDefinitions().forEach((def: FilterDefinition) => {
      let initialValue: any = '';

      if (def.type === 'checkbox') {
        initialValue = (initial[def.key] as string[]) || [];
      } else if (def.type === 'date' && initial[def.key]) {
        initialValue = initial[def.key];
      } else if (initial[def.key]) {
        initialValue = initial[def.key];
      }

      group[def.key] = new FormControl(initialValue);
    });

    this.form = new FormGroup(group);
  }

  getFormControl(key: string): FormControl {
    return this.form.get(key) as FormControl;
  }

  getSelectOptions(def: FilterDefinition): { value: string | number; description: string }[] {
    if (def.type !== 'select') return [];
    return def.options;
  }

  getCheckboxOptions(def: FilterDefinition): { value: string | number; description: string }[] {
    if (def.type !== 'checkbox') return [];
    return def.options;
  }

  onApply(): void {
    const value = this.getFilterModel();
    this.onChange(value);
    this.filterChange.emit(value);
    this.closed.emit();
  }

  onClear(): void {
    this.filterDefinitions().forEach((def) => {
      const control = this.form.get(def.key);
      if (control) {
        if (def.type === 'checkbox') {
          control.setValue([]);
        } else {
          control.setValue('');
        }
      }
    });
  }

  onCancel(): void {
    this.closed.emit();
  }

  onClose(): void {
    this.closed.emit();
  }

  getFilterModel(): FilterModel {
    const model: FilterModel = {};

    this.filterDefinitions().forEach((def) => {
      const control = this.form.get(def.key);
      if (control && control.value !== '' && !(Array.isArray(control.value) && control.value.length === 0)) {
        model[def.key] = control.value;
      }
    });

    return model;
  }

  hasActiveFilters(): boolean {
    return Object.keys(this.getFilterModel()).length > 0;
  }

  writeValue(value: FilterModel): void {
    if (value) {
      if (this.form) {
        this.filterDefinitions().forEach((def) => {
          const control = this.form.get(def.key);
          if (control && value[def.key] !== undefined) {
            control.setValue(value[def.key]);
          }
        });
      }
    }
  }

  registerOnChange(fn: (value: FilterModel) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
